import { html, nothing } from "lit";
import type { WorkspaceAgentCandidate } from "../chat/discover-workspace-agents.ts";
import type { AgentsListResult, SessionsListResult } from "../types.ts";
import { buildChatHistoryGroups } from "../chat/chat-history-catalog.ts";
import { renderFleetChatHistorySidebarMemo as renderFleetChatHistorySidebar } from "./fleet-chat-history-sidebar.ts";

export type AgentsImportPanelProps = {
  connected: boolean;
  sessions: SessionsListResult | null;
  sessionsLoading: boolean;
  selectedSessionKey: string;
  workspacePath: string | null;
  loading: boolean;
  busy: boolean;
  error: string | null;
  candidates: WorkspaceAgentCandidate[];
  selectedIds: string[];
  agentsList: AgentsListResult | null;
  historySearchQuery: string;
  historyShowArchived: boolean;
  onHistorySearchChange: (next: string) => void;
  onToggleHistoryArchived: () => void;
  onRefreshSessions: () => void;
  onSelectSession: (sessionKey: string) => void;
  onToggleAgent: (agentId: string) => void;
  onSelectAllNew: () => void;
  onClearSelection: () => void;
  onImport: () => void;
};

export function renderAgentsImportPanel(props: AgentsImportPanelProps) {
  const history = buildChatHistoryGroups({
    activeSessionKey: props.selectedSessionKey,
    openTabs: [],
    archivedKeys: [],
    sessions: props.sessions,
    includeArchived: props.historyShowArchived,
  });

  const newCount = props.candidates.filter((c) => !c.alreadyRegistered).length;

  return html`
    <div class="agents-import-layout">
      <aside class="agents-import-layout__sessions">
        ${renderFleetChatHistorySidebar({
          collapsed: false,
          loading: props.sessionsLoading,
          connected: props.connected,
          searchQuery: props.historySearchQuery,
          showArchived: props.historyShowArchived,
          groups: history.active,
          archived: history.archived,
          activeSessionKey: props.selectedSessionKey,
          canArchiveActive: false,
          onToggleCollapsed: () => undefined,
          onSearchQueryChange: props.onHistorySearchChange,
          onToggleShowArchived: props.onToggleHistoryArchived,
          onNewChat: () => undefined,
          onArchiveAndNew: () => undefined,
          onSelectSession: props.onSelectSession,
          onArchiveSession: () => undefined,
          onRestoreSession: () => undefined,
          onRenameSession: () => undefined,
          onDeleteSession: () => undefined,
          onRefresh: props.onRefreshSessions,
        })}
      </aside>
      <section class="agents-import-layout__main card">
        <div class="agents-import-layout__toolbar">
          <div>
            <div class="card-title">Load from chat</div>
            <div class="card-sub">
              Pick a Fleet Control session, then import agent definitions written to
              <span class="mono">agents/*.yaml</span> in that session workspace.
            </div>
          </div>
          <div class="row" style="gap: 8px;">
            <button
              type="button"
              class="btn btn--sm"
              ?disabled=${!props.connected || props.loading}
              @click=${props.onRefreshSessions}
            >
              Refresh chats
            </button>
            <button
              type="button"
              class="btn btn--sm primary"
              ?disabled=${props.busy || props.selectedIds.length === 0 || !props.connected}
              @click=${props.onImport}
            >
              ${props.busy ? "Importing…" : `Import selected (${props.selectedIds.length})`}
            </button>
          </div>
        </div>

        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : nothing
        }

        ${
          !props.selectedSessionKey
            ? html`
                <p class="muted agents-import-layout__hint">Select a chat on the left.</p>
              `
            : props.loading
              ? html`
                  <p class="muted agents-import-layout__hint">Scanning workspace…</p>
                `
              : props.candidates.length === 0
                ? html`
                    <p class="muted agents-import-layout__hint">
                      No <span class="mono">agents/*.yaml</span> files in this session workspace. Ask Fleet Control to
                      write agent configs under <span class="mono">agents/</span>, then refresh.
                    </p>
                  `
                : html`
                    <p class="muted agents-import-layout__meta">
                      Session <span class="mono">${props.selectedSessionKey}</span>
                      ${
                        props.workspacePath
                          ? html` · workspace <span class="mono">${props.workspacePath}</span>`
                          : nothing
                      }
                      · ${newCount} new · ${props.candidates.length - newCount} already registered
                    </p>
                    <div class="agents-import-layout__bulk">
                      <button type="button" class="btn btn--sm" @click=${props.onSelectAllNew}>
                        Select all new
                      </button>
                      <button type="button" class="btn btn--sm" @click=${props.onClearSelection}>
                        Clear
                      </button>
                    </div>
                    <div class="agents-import-list">
                      ${props.candidates.map((candidate) => renderCandidateRow(candidate, props))}
                    </div>
                  `
        }
      </section>
    </div>
  `;
}

function renderCandidateRow(candidate: WorkspaceAgentCandidate, props: AgentsImportPanelProps) {
  const checked = props.selectedIds.includes(candidate.id);
  const disabled = candidate.alreadyRegistered;
  return html`
    <label
      class="agents-import-row ${disabled ? "agents-import-row--registered" : ""} ${checked ? "agents-import-row--selected" : ""}"
    >
      <input
        type="checkbox"
        .checked=${checked}
        ?disabled=${disabled}
        @change=${() => props.onToggleAgent(candidate.id)}
      />
      <div class="agents-import-row__body">
        <div class="agents-import-row__title">
          ${candidate.name ?? candidate.id}
          <span class="mono muted">@${candidate.id}</span>
        </div>
        <div class="agents-import-row__meta muted">
          ${candidate.sourcePath}
          ${candidate.model ? html` · <span class="mono">${candidate.model}</span>` : nothing}
          ${candidate.skills?.length ? html` · ${candidate.skills.length} skills` : nothing}
        </div>
        ${
          candidate.purpose
            ? html`<p class="agents-import-row__purpose">${candidate.purpose.slice(0, 160)}</p>`
            : nothing
        }
      </div>
      ${
        disabled
          ? html`
              <span class="agents-import-row__badge">Registered</span>
            `
          : html`
              <span class="agents-import-row__badge agents-import-row__badge--new">New</span>
            `
      }
    </label>
  `;
}
